import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'antd';
import type { ChangeSet, ExistingTask, PlanSession } from '../../features/aiPlanner/plannerApi';
import ChatPanel from './ChatPanel';
import ChangeSetView, { type ChangeUI, type TouchUI } from './ChangeSetView';
import type { CoordinatorOption } from './TaskCards';
import { annotate, reannotate, strip } from './planUi';

interface Props {
  session: PlanSession;
  existing: ExistingTask[];
  coordinators: CoordinatorOption[];
  engagementDate?: string | null;
  onSend: (text: string, draft: ChangeSet) => Promise<unknown>;
  onRevert: (version: number) => Promise<unknown>;
  onApply: (final: ChangeSet) => void;
  onCancel: () => void;
  sending: boolean;
  applying: boolean;
}

const SUGGESTIONS: Record<string, string[]> = {
  add_program: ['Keep the additions to the minimum', 'Do not change the existing tasks, only add', 'Give the new tasks to different people'],
  remove_program: ['Remove only what belongs to this program', 'Also shrink the food and seating numbers', 'Tell the invited guests it is cancelled'],
};

function toUi(cs: ChangeSet | null | undefined, prev?: ChangeUI): ChangeUI {
  const keep = (arr: TouchUI[] | undefined, old?: TouchUI[]) =>
    (arr ?? []).map((x) => ({ ...x, _byUser: old?.find((o) => o.task_id === x.task_id)?._byUser || x._byUser }));
  return {
    add: prev ? reannotate(cs?.add, prev.add) : annotate(cs?.add),
    modify: keep(cs?.modify, prev?.modify),
    remove: keep(cs?.remove, prev?.remove),
    flag: keep(cs?.flag, prev?.flag),
  };
}

function toDoc(base: ChangeSet, ui: ChangeUI): ChangeSet {
  const clean = (arr: TouchUI[]) => arr.filter((x) => !x._dropped).map(({ _dropped, ...rest }) => { void _dropped; return rest; });
  return { ...base, add: strip(ui.add), modify: clean(ui.modify), remove: clean(ui.remove), flag: clean(ui.flag) };
}

/** Review the proposed changes (left) and refine them in conversation (right). */
export default function ChangeStep({
  session, existing, coordinators, engagementDate, onSend, onRevert, onApply, onCancel, sending, applying,
}: Props) {
  const cs = session.change_set ?? { summary: '', add: [], modify: [], remove: [], flag: [], notes: [] };
  const [ui, setUi] = useState<ChangeUI>(() => toUi(cs));
  const version = session.revisions?.[session.revisions.length - 1]?.version ?? 0;
  useEffect(() => {
    setUi((prev) => toUi(session.change_set, prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const existingMap = useMemo(() => new Map(existing.map((t) => [t.id, t])), [existing]);
  const doc = () => toDoc(cs, ui);
  const counts = {
    add: ui.add.reduce((n, w) => n + w.tasks.filter((t) => !t._dropped && t.title.trim()).reduce((m, t) => m + 1 + t.subtasks.filter((s) => !s._dropped && s.title.trim()).length, 0), 0),
    modify: ui.modify.filter((x) => !x._dropped).length,
    remove: ui.remove.filter((x) => !x._dropped).length,
    flag: ui.flag.length,
  };
  const nothing = counts.add + counts.modify + counts.remove === 0;

  const confirm = () => {
    Modal.confirm({
      title: 'Apply these changes to the engagement?',
      content: (
        <ul className="ml-4 mt-2 list-disc text-sm">
          <li>{counts.add} task(s) and sub-task(s) will be added</li>
          <li>{counts.modify} existing task(s) will be changed</li>
          <li>{counts.remove} task(s) will be deleted</li>
          {counts.flag > 0 && <li>{counts.flag} completed / in-progress task(s) stay as they are, marked as no longer needed</li>}
        </ul>
      ),
      okText: 'Apply changes', cancelText: 'Not yet', onOk: () => onApply(doc()),
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-line-soft bg-bg/60 px-3 py-2 text-xs text-ink-secondary">
        <span>Add <span className="font-semibold text-ink">{counts.add}</span></span>
        <span>Change <span className="font-semibold text-ink">{counts.modify}</span></span>
        <span>Remove <span className="font-semibold text-ink">{counts.remove}</span></span>
        <span>No longer needed <span className="font-semibold text-[#B91C1C]">{counts.flag}</span></span>
        <span className="ml-auto">Version {version || 1}</span>
      </div>

      <div className="flex h-[calc(100vh-270px)] min-h-[480px] flex-col overflow-hidden rounded-lg border border-line-soft lg:flex-row">
        <div className="min-h-0 flex-[5] overflow-y-auto bg-bg/40 px-5 py-3">
          <ChangeSetView value={ui} onChange={setUi} existing={existingMap} coordinators={coordinators}
            eventDate={engagementDate} notes={cs.notes ?? []} />
        </div>
        <div className="min-h-[260px] flex-[3] border-t border-line-soft lg:min-w-[360px] lg:max-w-[460px] lg:border-l lg:border-t-0">
          <ChatPanel messages={session.messages ?? []} revisions={session.revisions ?? []}
            suggestions={SUGGESTIONS[session.mode] ?? []} sending={sending}
            onSend={(t) => onSend(t, doc())} onRevert={onRevert} />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <span className="text-xs text-ink-secondary">Nothing changes in the engagement until you apply.</span>
        <div className="flex gap-2">
          <Button onClick={onCancel} disabled={applying}>Cancel</Button>
          <Button type="primary" loading={applying} disabled={nothing || sending} onClick={confirm}>Apply changes</Button>
        </div>
      </div>
    </div>
  );
}
